import { create } from "zustand";
import { api } from "@/lib/api";
import type {
  ReadDefaultPromptDto,
  ReadPromptDto,
  UpdatePromptDto
} from "@package/types";
import type { AsyncState } from "./types";

interface PromptsState extends AsyncState {
  prompts: ReadPromptDto[];
  defaultContent: string;
}

interface PromptsActions {
  loadPrompts: () => Promise<void>;
  loadDefault: () => Promise<void>;
  createPrompt: (name: string) => Promise<void>;
  updatePrompt: (id: number, data: UpdatePromptDto) => Promise<void>;
  deletePrompt: (id: number) => Promise<void>;
  reset: () => void;
}

type PromptsStore = PromptsState & PromptsActions;

export const usePromptsStore = create<PromptsStore>()((set, get) => ({
  prompts: [],
  defaultContent: "",
  isLoading: false,
  isLoaded: false,
  error: null,

  loadPrompts: async () => {
    set({ isLoading: true, error: null });
    try {
      const data = await api.get<ReadPromptDto[]>("/prompts");
      set({ prompts: data, isLoading: false, isLoaded: true });
    } catch (error) {
      set({ isLoading: false, error: (error as Error).message });
    }
  },

  loadDefault: async () => {
    if (get().defaultContent) return;
    try {
      const data = await api.get<ReadDefaultPromptDto>("/prompts/default");
      set({ defaultContent: data.content });
    } catch (error) {
      set({ error: (error as Error).message });
    }
  },

  createPrompt: async (name: string) => {
    try {
      const prompt = await api.post<ReadPromptDto>("/prompts", { name });
      set((state) => ({ prompts: [prompt, ...state.prompts] }));
    } catch (error) {
      set({ error: (error as Error).message });
      throw error;
    }
  },

  updatePrompt: async (id: number, data: UpdatePromptDto) => {
    try {
      const updated = await api.patch<ReadPromptDto>(`/prompts/${id}`, data);
      set((state) => ({
        prompts: data.isActive
          ? state.prompts.map((p) =>
              p.id === id ? updated : { ...p, isActive: false }
            )
          : state.prompts.map((p) => (p.id === id ? updated : p))
      }));
    } catch (error) {
      set({ error: (error as Error).message });
      throw error;
    }
  },

  deletePrompt: async (id: number) => {
    try {
      await api.delete(`/prompts/${id}`);
      set((state) => ({
        prompts: state.prompts.filter((p) => p.id !== id)
      }));
    } catch (error) {
      set({ error: (error as Error).message });
      throw error;
    }
  },

  reset: () =>
    set({
      prompts: [],
      defaultContent: "",
      isLoading: false,
      isLoaded: false,
      error: null
    })
}));
